import React from 'react';
import { Shield, FileText, DollarSign, Truck, Clock, AlertCircle } from 'lucide-react';
import Header1 from '../common/Header1';
import Footer1 from '../common/Footer1';
import ScrollManager from '../layout/scrollManager';
import SideContactBar from '../layout/SideContactBar';
import SEOHead from '../layout/SEOHead';
import { SITE_URL } from '../../config/seo';

const termsSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/terms#webpage`,
    name: 'Terms & Conditions – Urban Shine Laundry',
    url: `${SITE_URL}/terms`,
    description: 'Terms and Conditions for Urban Shine Laundry services including pricing, pickup, delivery, and liability.',
    datePublished: '2025-06-01',
    dateModified: '2025-06-01',
    breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Terms & Conditions', item: `${SITE_URL}/terms` },
        ],
    },
};

const TermsAndConditions = () => {
    return (
        <ScrollManager>
            <SEOHead
                title="Terms & Conditions – Urban Shine Laundry | New Town, Kolkata"
                description="Read the Terms & Conditions for Urban Shine Laundry. Understand our service policies, pricing, pickup & delivery terms, liability, and your rights as a customer."
                keywords="Urban Shine Laundry terms and conditions, laundry service terms Kolkata"
                canonicalPath="/terms"
                ogType="article"
                structuredData={termsSchema}
                publishedTime="2025-06-01T00:00:00+05:30"
            />
            <Header1 />
            <SideContactBar />
            <br />
            <br />
            <br />
            <section className="py-16 md:py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    {/* Section Header */}
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-neutral-100 text-neutral-700 rounded-full text-sm font-medium mb-4">Legal</span>
                        <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">Terms & Conditions</h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            Please read these terms and conditions carefully before using our laundry services.
                            By using our services, you agree to comply with and be bound by the following terms and conditions.
                        </p>
                    </div>

                    {/* Main Content */}
                    <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-16">
                        {/* Terms Categories Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                            <div className="flex flex-col items-center text-center p-6 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
                                <div className="p-4 rounded-full bg-green-600 text-white mb-4">
                                    <DollarSign size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">Pricing & Payment</h3>
                                <p className="text-gray-600">Terms related to our service costs, payment methods, and billing procedures.</p>
                            </div>

                            <div className="flex flex-col items-center text-center p-6 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors">
                                <div className="p-4 rounded-full bg-neutral-500 text-white mb-4">
                                    <Truck size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">Delivery & Pickup</h3>
                                <p className="text-gray-600">Terms for our delivery services, timelines, and minimum order requirements.</p>
                            </div>

                            <div className="flex flex-col items-center text-center p-6 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
                                <div className="p-4 rounded-full bg-green-600 text-white mb-4">
                                    <Shield size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">Service Guarantees</h3>
                                <p className="text-gray-600">Our commitments to quality and what you can expect from our services.</p>
                            </div>
                        </div>

                        {/* Terms Sections */}
                        <div className="space-y-12">
                            {/* General Terms */}
                            <div>
                                <h2 className="text-2xl font-bold text-neutral-500 border-b border-gray-200 pb-4 mb-6">General Terms</h2>
                                <ul className="space-y-4">
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-1">1.</span>
                                        <p className="text-gray-700">All prices are inclusive of taxes.</p>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-1">2.</span>
                                        <p className="text-gray-700">Minimum order value of ₹250 for free pickup and delivery.</p>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-1">3.</span>
                                        <p className="text-gray-700">Actual dry cleaning prices may vary depending on the actual garment (depending on fabric, texture, embellishments, level of embroidery or similar work, etc.)</p>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-1">4.</span>
                                        <p className="text-gray-700">Minimum 5 Kg of garments are required to qualify the KG based services.</p>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-3 mt-1">5.</span>
                                        <p className="text-gray-700">We reserve the right to refuse service for heavily soiled or damaged garments.</p>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Important Notice Box */}
                        <div className="mt-12 p-6 bg-green-50 border-l-4 border-green-600 rounded-r-lg">
                            <div className="flex items-start">
                                <AlertCircle size={24} className="text-green-600 mr-4 mt-1 flex-shrink-0" />
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 mb-2">Important Notice</h4>
                                    <p className="text-gray-700">
                                        These terms and conditions are subject to change without prior notice. The latest version will always be available on our website and app. By continuing to use our services, you acknowledge and agree to any updates to these terms.
                                    </p>
                                </div>
                            </div>
                        </div>
                        {/* Last Updated */}
                        <div className="text-center mt-12 text-gray-500">
                            <p>Last Updated: April 2025</p>
                        </div>
                    </div>
                </div>
            </section>
            <Footer1 />
        </ScrollManager>
    );
};

export default TermsAndConditions;